/**
 * Additional Installations Page
 *
 * Manages optional local AI components like Ollama, Fooocus, etc.
 */

import React, { useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';

import { useComponents } from '../../hooks/useComponents';
import { useHardwareCapabilities } from '../../hooks/useHardwareCapabilities';
import { useOllamaModels } from '../../hooks/useOllamaModels';

import { ComponentCard } from './ComponentCard';
import { HardwareInfoCard } from './HardwareInfoCard';
import { OllamaModelManager } from '../../components/ollama/OllamaModelManager';
import { ImageModelManager } from '../../components/image-models/ImageModelManager';
import { WanVaceCard } from '../../components/WanVace/WanVaceCard';
import { WanLocalInstallSection } from './WanLocalInstallSection';

// Component cards whose "Manage Models" button opens the local image-checkpoint
// manager. Maps the setup component id -> backend image provider id.
const IMAGE_MODEL_COMPONENTS: Record<string, string> = {
  comfyui: 'comfyui-sd',
  fooocus: 'fooocus',
};

export const AdditionalInstallationsPage: React.FC = () => {
  const { t } = useTranslation();
  const { hardware, loading: hwLoading, refresh: refreshHardware } = useHardwareCapabilities();
  const {
    components,
    loading: compLoading,
    refresh,
    installComponent,
    uninstallComponent,
    dismissInstallError,
    fetchInstallLog,
    installProgress,
  } = useComponents();

  const [showModelManager, setShowModelManager] = useState(false);
  const [imageManager, setImageManager] = useState<{ provider: string; title: string } | null>(
    null
  );
  const {
    models: ollamaModels,
    loading: ollamaLoading,
    ollamaAvailable,
    downloadProgress,
    downloadModel,
    deleteModel,
    clearProgress,
    refreshModels,
  } = useOllamaModels();

  const handleRefresh = async () => {
    await Promise.all([refresh(), refreshHardware()]);
  };

  // Sort components by category. Wan VACE is rendered by its dedicated card
  // (multi-model + LoRA management), so filter it out of the generic list to
  // avoid showing the component twice.
  const sortedComponents = Object.values(components)
    .filter((c) => c.id !== 'wan_vace')
    .sort((a, b) => {
      const categoryOrder = ['llm', 'image', 'audio', 'translation', 'tts'];
      const aIndex = categoryOrder.indexOf(a.category);
      const bIndex = categoryOrder.indexOf(b.category);
      if (aIndex !== bIndex) return aIndex - bIndex;
      return a.name.localeCompare(b.name);
    });

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-secondary-900 dark:text-secondary-100">
            {t('additionalInstallations.title', 'Additional Installations')}
          </h1>
          <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
            {t(
              'additionalInstallations.description',
              'Install optional local AI components for enhanced performance and privacy.'
            )}
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={compLoading}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-secondary-700 dark:text-secondary-300 bg-white dark:bg-secondary-800 border border-secondary-300 dark:border-secondary-600 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors disabled:opacity-50"
        >
          <svg
            className={`w-4 h-4 mr-2 ${compLoading ? 'animate-spin' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          {t('additionalInstallations.refresh', 'Refresh')}
        </button>
      </div>

      {/* Hardware Info */}
      <HardwareInfoCard hardware={hardware} loading={hwLoading} />

      {/* Recommendations */}
      {hardware?.recommendedComponents && hardware.recommendedComponents.length > 0 && (
        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg mb-6">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-green-800 dark:text-green-200 text-sm">
              <span className="font-semibold">
                {t(
                  'additionalInstallations.recommendedForYourSystem',
                  'Recommended for your system'
                )}
                :
              </span>{' '}
              {hardware.recommendedComponents
                .map((id: string) => {
                  const comp = components[id];
                  return comp?.name || id;
                })
                .join(', ')}
            </p>
          </div>
        </div>
      )}

      {/* Loading state */}
      {compLoading && Object.keys(components).length === 0 && (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="border border-secondary-200 dark:border-secondary-700 rounded-lg p-4 animate-pulse"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-secondary-200 dark:bg-secondary-700 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-4 bg-secondary-200 dark:bg-secondary-700 rounded w-1/3 mb-2"></div>
                  <div className="h-3 bg-secondary-200 dark:bg-secondary-700 rounded w-2/3"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Components List */}
      <div className="space-y-4">
        {sortedComponents.map((component) => (
          <ComponentCard
            key={component.id}
            component={component}
            hardware={hardware}
            progress={installProgress[component.id]}
            onInstall={() => installComponent(component.id)}
            onUninstall={() => uninstallComponent(component.id)}
            onDismissError={() => dismissInstallError(component.id)}
            onFetchLog={() => fetchInstallLog(component.id)}
            onManageModels={
              component.id === 'ollama'
                ? () => setShowModelManager(true)
                : IMAGE_MODEL_COMPONENTS[component.id]
                  ? () =>
                      setImageManager({
                        provider: IMAGE_MODEL_COMPONENTS[component.id],
                        title: component.name,
                      })
                  : undefined
            }
          />
        ))}
        {/* Wan VACE: dedicated card for multi-model + LoRA management */}
        <WanVaceCard />

        {/* Wan local video generation install cards (Phase 4) */}
        <WanLocalInstallSection />
      </div>

      {/* Empty state */}
      {!compLoading && Object.keys(components).length === 0 && (
        <div className="text-center py-12">
          <svg
            className="w-12 h-12 text-secondary-400 mx-auto mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
            />
          </svg>
          <p className="text-secondary-600 dark:text-secondary-400">
            {t('additionalInstallations.noComponentsAvailable', 'No components available')}
          </p>
          <button
            onClick={handleRefresh}
            className="mt-4 text-primary-600 dark:text-primary-400 hover:underline"
          >
            {t('additionalInstallations.tryAgain', 'Try again')}
          </button>
        </div>
      )}

      {/* Footer info */}
      <div className="mt-8 p-4 bg-secondary-50 dark:bg-secondary-800/50 rounded-lg">
        <div className="flex items-start gap-3">
          <svg
            className="w-5 h-5 text-secondary-500 flex-shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div className="text-sm text-secondary-600 dark:text-secondary-400">
            <p className="font-medium mb-1">
              {t('additionalInstallations.localFirstInfo', 'Local-First Architecture')}
            </p>
            <p>
              {t(
                'additionalInstallations.localFirstDescription',
                'Components installed locally run entirely on your machine, providing better privacy, faster processing, and no cloud costs. When local components are unavailable, the system falls back to cloud services.'
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Ollama Model Manager Modal */}
      <OllamaModelManager
        isOpen={showModelManager}
        onClose={() => setShowModelManager(false)}
        models={ollamaModels}
        loading={ollamaLoading}
        ollamaAvailable={ollamaAvailable}
        downloadProgress={downloadProgress}
        hardware={hardware}
        onDownload={downloadModel}
        onDelete={deleteModel}
        onRefresh={refreshModels}
        onClearProgress={clearProgress}
      />

      {/* Local Image-Model Manager Modal (Fooocus SDXL / ComfyUI SD 1.5) */}
      {imageManager && (
        <ImageModelManager
          isOpen={true}
          onClose={() => setImageManager(null)}
          provider={imageManager.provider}
          title={imageManager.title}
        />
      )}
    </div>
  );
};

export default AdditionalInstallationsPage;
